import { useState } from 'react'

// ── Types ────────────────────────────────────────────────────────────────────

export interface SuggestionParam {
  label: string
  value: string
  sublabel?: string
  color?: string            // tailwind text color class, default 'text-ink'
}

export interface SuggestionImpact {
  label: string
  value: string
  color?: string            // tailwind text color class
}

export interface SuggestionCardProps {
  /** Action title shown in collapsed header, e.g. "Open short perp hedge" */
  action: string
  /** Formatted amount string, e.g. "+$859.55" or "−$506.95" */
  amount: string
  /** Optional percent string shown after amount, e.g. "(-9.8%)" */
  pctChange?: string
  /** Color accent: true = yellow (add/increase), false = orange (reduce/remove) */
  isPositive: boolean
  /** Explanation text revealed on expand */
  description: string
  /** Key-value parameter cards (e.g. leverage, SL, margin) */
  parameters?: SuggestionParam[]
  /** Impact metrics (e.g. APY, daily earn, risk) */
  impacts?: SuggestionImpact[]
  /** Signal/reason code badges */
  signals?: string[]
}

// ── Component ────────────────────────────────────────────────────────────────

export default function SuggestionCard({
  action, amount, pctChange, isPositive, description,
  parameters, impacts, signals,
}: SuggestionCardProps) {
  const [open, setOpen] = useState(false)

  const borderCls  = isPositive ? 'border-yellow-400/20' : 'border-orange-400/20'
  const bgCls      = isPositive ? 'bg-yellow-400/[0.03]' : 'bg-orange-400/[0.03]'
  const amountCls  = isPositive ? 'text-amber-400' : 'text-orange-400'
  const dotCls     = isPositive ? 'bg-yellow-400' : 'bg-orange-400'

  return (
    <div className={`rounded-lg border ${borderCls} ${bgCls} overflow-hidden transition-all`}>
      {/* ── Collapsed header: action + amount ─────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left cursor-pointer select-none group hover:bg-surface-hover transition-colors"
      >
        <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
        <span className="flex-1 min-w-0">
          <span className="text-[11px] font-semibold text-ink leading-tight">{action}</span>
        </span>
        <span className="shrink-0 flex items-center gap-1">
          <span className={`text-xs font-bold ${amountCls}`}>{amount}</span>
          {pctChange && (
            <span className={`text-[10px] font-medium ${amountCls}/70`}>{pctChange}</span>
          )}
        </span>
        <span className={`text-[10px] text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {/* ── Expanded body ──────────────────────────────────────────────── */}
      {open && (
        <div className="px-3 pb-3 flex flex-col gap-2.5 border-t border-fringe pt-2.5">
          {/* Description */}
          <p className="text-[10px] text-ink-muted leading-relaxed">{description}</p>

          {/* Parameters grid */}
          {parameters && parameters.length > 0 && (
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">Parameters</div>
              <div className={`grid gap-1.5 ${parameters.length <= 3 ? `grid-cols-${parameters.length}` : 'grid-cols-3'}`}>
                {parameters.map(p => (
                  <div key={p.label} className="bg-surface-hover rounded-md px-2 py-1.5">
                    <div className="text-[8px] text-gray-500 uppercase tracking-wide">{p.label}</div>
                    <div className={`text-[11px] font-semibold ${p.color ?? 'text-ink'}`}>{p.value}</div>
                    {p.sublabel && <div className="text-[8px] text-gray-500">{p.sublabel}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Impact row */}
          {impacts && impacts.length > 0 && (
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">Impact</div>
              <div className="flex items-center gap-3">
                {impacts.map(imp => (
                  <div key={imp.label}>
                    <div className="text-[8px] text-gray-500">{imp.label}</div>
                    <div className={`text-[11px] font-semibold ${imp.color ?? 'text-ink'}`}>{imp.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Signal badges */}
          {signals && signals.length > 0 && (
            <div>
              <div className="text-[9px] text-gray-500 uppercase tracking-wide mb-1">Signals</div>
              <div className="flex flex-wrap gap-1">
                {signals.map(s => (
                  <span key={s} className="text-[8px] px-1.5 py-0.5 rounded-full bg-accent-purple/10 border border-accent-purple/20 text-accent-purple/80">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
